/**
 * Types for PDF Template Mapper
 */

export interface PdfTemplate {
  id: string;
  bankName: string;
  templateName: string;
  description?: string;
  filePath: string;
  fileSize: number;
  pageCount: number;
  pageWidth: number;
  pageHeight: number;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PdfTemplateMapping {
  id: string;
  templateId: string;
  fieldName: FieldName;
  fieldType: FieldType;
  pageNumber: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  textAlign: 'left' | 'center' | 'right';
  lineHeight: number;
  maxLines?: number;
  formatPattern?: string;
  createdAt: string;
}

export interface PdfTemplateTableColumn {
  id: string;
  templateId: string;
  mappingId: string;
  columnName: ColumnName;
  columnOrder: number;
  xOffset: number;
  width: number;
  textAlign: 'left' | 'center' | 'right';
  formatPattern?: string;
}

export interface PdfTemplateWithMappings extends PdfTemplate {
  mappings: PdfTemplateMapping[];
  tableColumns: PdfTemplateTableColumn[];
}

export interface PdfTemplateListItem {
  id: string;
  bankName: string;
  templateName: string;
  description?: string;
  pageCount: number;
  isActive: boolean;
  createdAt: string;
  mappingComplete: boolean;
  mappingCount: number;
}

// Field types
export type FieldName =
  | 'bankName'
  | 'bankAddress'
  | 'accountHolderName'
  | 'accountHolderAddress'
  | 'accountHolderAddressLine1'
  | 'accountHolderAddressLine2'
  | 'accountHolderAddressLine3'
  | 'accountNumber'
  | 'checkingAccountNumber'
  | 'savingsAccountNumber'
  | 'routingNumber'
  | 'statementPeriodStart'
  | 'statementPeriodEnd'
  | 'statementPeriod'
  | 'openingBalance'
  | 'depositsAndAdditions'
  | 'atmDebitSubtractions'
  | 'otherSubtractions'
  | 'checksAmount'
  | 'serviceFees'
  | 'closingBalance'
  | 'checkingOpeningBalance'
  | 'checkingDepositsAndAdditions'
  | 'checkingAtmDebitSubtractions'
  | 'checkingOtherSubtractions'
  | 'checkingChecksAmount'
  | 'checkingServiceFees'
  | 'checkingClosingBalance'
  | 'savingsOpeningBalance'
  | 'savingsDepositsAndAdditions'
  | 'savingsAtmDebitSubtractions'
  | 'savingsOtherSubtractions'
  | 'savingsServiceFees'
  | 'savingsClosingBalance'
  | 'savingsInterestEarned'
  | 'checkingEndingBalanceSummary'
  | 'savingsEndingBalanceSummary'
  | 'totalBalance'
  | 'totalDeposits'
  | 'totalWithdrawals'
  | 'numberOfDeposits'
  | 'numberOfWithdrawals'
  | 'transactionTable'
  | 'transactionTableHeader'
  | 'transactionDate'
  | 'transactionDescription'
  | 'transactionAmount'
  | 'pageNumber'
  | 'totalPages'
  | 'customField1'
  | 'customField2'
  | 'customField3'
  | 'customField4'
  | 'customField5';

export type FieldType = 'text' | 'date' | 'currency' | 'number' | 'table';

export type ColumnName =
  | 'date'
  | 'description'
  | 'reference'
  | 'checkNumber'
  | 'debit'
  | 'credit'
  | 'amount'
  | 'balance';

// UI State types
export interface MappingRegion {
  id: string;
  fieldName: FieldName;
  fieldType: FieldType;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  textAlign: 'left' | 'center' | 'right';
  lineHeight: number;
  maxLines?: number;
  formatPattern?: string;
  isNew?: boolean; // Client-side only flag
}

export interface TableColumnConfig {
  id: string;
  columnName: ColumnName;
  columnOrder: number;
  xOffset: number;
  width: number;
  textAlign: 'left' | 'center' | 'right';
  formatPattern?: string;
}

// Field definitions for the palette
export interface FieldDefinition {
  name: FieldName;
  label: string;
  type: FieldType;
  icon: string;
  description: string;
}

export const AVAILABLE_FIELDS: FieldDefinition[] = [
  { name: 'accountHolderName', label: 'Account Holder Name', type: 'text', icon: '👤', description: 'Full name of account holder' },
  { name: 'accountHolderAddress', label: 'Full Address', type: 'text', icon: '🏠', description: 'Complete address on one line' },
  { name: 'accountHolderAddressLine1', label: 'Address Line 1', type: 'text', icon: '📍', description: 'Street address' },
  { name: 'accountHolderAddressLine2', label: 'Address Line 2', type: 'text', icon: '📍', description: 'City, State' },
  { name: 'accountHolderAddressLine3', label: 'Address Line 3', type: 'text', icon: '📍', description: 'ZIP code' },
  { name: 'accountNumber', label: 'Account Number', type: 'text', icon: '#️⃣', description: 'Bank account number' },
  { name: 'checkingAccountNumber', label: 'Checking Account #', type: 'text', icon: '#️⃣', description: 'Checking account number' },
  { name: 'savingsAccountNumber', label: 'Savings Account #', type: 'text', icon: '#️⃣', description: 'Savings account number' },
  { name: 'routingNumber', label: 'Routing Number', type: 'text', icon: '🔢', description: 'Bank routing number' },
  { name: 'statementPeriodStart', label: 'Period Start', type: 'date', icon: '📅', description: 'Statement start date' },
  { name: 'statementPeriodEnd', label: 'Period End', type: 'date', icon: '📅', description: 'Statement end date' },
  { name: 'statementPeriod', label: 'Statement Period', type: 'text', icon: '📆', description: 'Combined date range' },
  { name: 'openingBalance', label: 'Opening Balance', type: 'currency', icon: '💰', description: 'Beginning balance' },
  { name: 'depositsAndAdditions', label: 'Deposits & Additions', type: 'currency', icon: '➕', description: 'Deposits and other additions' },
  { name: 'atmDebitSubtractions', label: 'ATM/Debit Subtractions', type: 'currency', icon: '💳', description: 'ATM and debit card subtractions' },
  { name: 'otherSubtractions', label: 'Other Subtractions', type: 'currency', icon: '➖', description: 'Other subtractions' },
  { name: 'checksAmount', label: 'Checks Amount', type: 'currency', icon: '📝', description: 'Total checks amount' },
  { name: 'serviceFees', label: 'Service Fees', type: 'currency', icon: '💲', description: 'Service fees charged' },
  { name: 'closingBalance', label: 'Closing Balance', type: 'currency', icon: '💵', description: 'Ending balance' },
  { name: 'checkingOpeningBalance', label: 'Checking Opening', type: 'currency', icon: '💰', description: 'Checking beginning balance' },
  { name: 'checkingDepositsAndAdditions', label: 'Checking Deposits', type: 'currency', icon: '➕', description: 'Checking deposits & additions' },
  { name: 'checkingAtmDebitSubtractions', label: 'Checking ATM/Debit', type: 'currency', icon: '💳', description: 'Checking ATM/debit subtractions' },
  { name: 'checkingOtherSubtractions', label: 'Checking Other Sub', type: 'currency', icon: '➖', description: 'Checking other subtractions' },
  { name: 'checkingChecksAmount', label: 'Checking Checks', type: 'currency', icon: '📝', description: 'Checking checks amount' },
  { name: 'checkingServiceFees', label: 'Checking Fees', type: 'currency', icon: '💲', description: 'Checking service fees' },
  { name: 'checkingClosingBalance', label: 'Checking Closing', type: 'currency', icon: '💵', description: 'Checking ending balance' },
  { name: 'savingsOpeningBalance', label: 'Savings Opening', type: 'currency', icon: '💰', description: 'Savings beginning balance' },
  { name: 'savingsDepositsAndAdditions', label: 'Savings Deposits', type: 'currency', icon: '➕', description: 'Savings deposits & additions' },
  { name: 'savingsAtmDebitSubtractions', label: 'Savings ATM/Debit', type: 'currency', icon: '💳', description: 'Savings ATM/debit subtractions' },
  { name: 'savingsOtherSubtractions', label: 'Savings Other Sub', type: 'currency', icon: '➖', description: 'Savings other subtractions' },
  { name: 'savingsServiceFees', label: 'Savings Fees', type: 'currency', icon: '💲', description: 'Savings service fees' },
  { name: 'savingsClosingBalance', label: 'Savings Closing', type: 'currency', icon: '💵', description: 'Savings ending balance' },
  { name: 'savingsInterestEarned', label: 'Savings Interest', type: 'currency', icon: '📈', description: 'Interest earned on savings' },
  { name: 'checkingEndingBalanceSummary', label: 'Checking Balance (Pg1)', type: 'currency', icon: '💵', description: 'Checking ending balance on page 1 summary' },
  { name: 'savingsEndingBalanceSummary', label: 'Savings Balance (Pg1)', type: 'currency', icon: '💵', description: 'Savings ending balance on page 1 summary' },
  { name: 'totalBalance', label: 'Total Balance', type: 'currency', icon: '💰', description: 'Combined balance all accounts' },
  { name: 'totalDeposits', label: 'Total Deposits', type: 'currency', icon: '📈', description: 'Sum of credits' },
  { name: 'totalWithdrawals', label: 'Total Withdrawals', type: 'currency', icon: '📉', description: 'Sum of debits' },
  { name: 'numberOfDeposits', label: '# of Deposits', type: 'number', icon: '➕', description: 'Count of deposits' },
  { name: 'numberOfWithdrawals', label: '# of Withdrawals', type: 'number', icon: '➖', description: 'Count of withdrawals' },
  { name: 'transactionTable', label: 'Transaction Table', type: 'table', icon: '📊', description: 'Transaction list area' },
  { name: 'transactionTableHeader', label: 'Table Header Row', type: 'text', icon: '📋', description: 'Column headers (Date, Description, Amount)' },
  { name: 'transactionDate', label: 'Transaction Date', type: 'date', icon: '📅', description: 'Date column for transactions' },
  { name: 'transactionDescription', label: 'Transaction Desc', type: 'text', icon: '📝', description: 'Description column for transactions' },
  { name: 'transactionAmount', label: 'Transaction Amount', type: 'currency', icon: '💵', description: 'Amount column for transactions' },
  { name: 'pageNumber', label: 'Page Number', type: 'number', icon: '📄', description: 'Current page' },
  { name: 'totalPages', label: 'Total Pages', type: 'number', icon: '📑', description: 'Total page count' },
  { name: 'bankName', label: 'Bank Name', type: 'text', icon: '🏦', description: 'Name of the bank' },
  { name: 'bankAddress', label: 'Bank Address', type: 'text', icon: '🏢', description: 'Bank address' },
  { name: 'customField1', label: 'Custom Field 1', type: 'text', icon: '✏️', description: 'Custom text field' },
  { name: 'customField2', label: 'Custom Field 2', type: 'text', icon: '✏️', description: 'Custom text field' },
  { name: 'customField3', label: 'Custom Field 3', type: 'text', icon: '✏️', description: 'Custom text field' },
];

export const COLUMN_DEFINITIONS: { name: ColumnName; label: string; defaultAlign: 'left' | 'right' }[] = [
  { name: 'date', label: 'Date', defaultAlign: 'left' },
  { name: 'description', label: 'Description', defaultAlign: 'left' },
  { name: 'reference', label: 'Reference', defaultAlign: 'left' },
  { name: 'checkNumber', label: 'Check #', defaultAlign: 'left' },
  { name: 'debit', label: 'Debit', defaultAlign: 'right' },
  { name: 'credit', label: 'Credit', defaultAlign: 'right' },
  { name: 'amount', label: 'Amount', defaultAlign: 'right' },
  { name: 'balance', label: 'Balance', defaultAlign: 'right' },
];

export const FONT_FAMILIES = [
  // ITC Franklin Gothic (Bank of America / Chase style)
  'ITCFranklinGothicStd-Book',
  'ITCFranklinGothicStd-Demi',
  'ITCFranklinGothicStd-Med',
  'ITCFranklinGothicStd-Hvy',
  'ITCFranklinGothicStd-BkCd',
  'ITCFranklinGothicStd-DmCd',
  // Franklin Gothic
  'Franklin Gothic',
  // Standard PDF fonts
  'Helvetica',
  'Helvetica-Bold',
  'Times-Roman',
  'Times-Bold',
  'Courier',
  'Courier-Bold',
  'Arial',
  'Arial-Bold',
];

export const FONT_SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24];

export const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
  { value: 'MM/DD/YY', label: 'MM/DD/YY (12/31/25)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2025)' },
  { value: 'MMMM DD, YYYY', label: 'MMMM DD, YYYY (December 31, 2025)' },
];

export const CURRENCY_FORMATS = [
  { value: '$#,##0.00', label: '$1,234.56' },
  { value: '#,##0.00', label: '1,234.56 (no symbol)' },
  { value: '$#,##0', label: '$1,235 (no cents)' },
  { value: '($#,##0.00)', label: '($1,234.56) for negative' },
];
